import { Controller } from '@hotwired/stimulus';

export default class ChartjsController extends Controller {
  static values = {
    data: Object,
    metaOptions: Object,
    type: String
  };

  connect() {
    const type = this.typeValue;
    const metaOptions = this.metaOptionsValue;
    const data = this.dataValue;

    let options
    if (type === 'line') {
      options = this.#lineOptions(metaOptions);
    } else  if (type === 'doughnut') {
      options = this.#doughnutOptions();
    } else {
      options = {};
    }

    new Chart(this.element, {
      type,
      data,
      options
    });
  }

  // Private methods

  #doughnutOptions() {
    return {
      cutout: '62%',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#FFFFFF',
          borderColor: '#D9DDF0',
          borderWidth: 1,
          bodyColor: '#1A1917',
          bodyFont: {
            family: 'Sofia Pro, sans-serif'
          },
          padding: {
            left: 12,
            right: 12,
            top: 8,
            bottom: 8
          },
          displayColors: true,
          callbacks: {
            label: (context) => {
              const label = context.label || context.dataset?.label || '';
              return [` ${label}:`, ` ${context.raw} %`];
            }
          }
        }
      }
    };
  }

  #lineOptions(metaOptions) {
    const labelsFromMeta = metaOptions.tooltips

    const external = (context) => {
      const { chart, tooltip } = context

      let el = chart.canvas.parentNode.querySelector('.cf-tooltip')
      if (!el) {
        el = document.createElement('div');
        el.className = 'cf-tooltip';
        el.style.position = 'absolute';
        el.style.transform = 'translate(-50%, -100%)';
        el.style.pointerEvents = 'none';
        el.style.zIndex = '10';
        el.style.width = 'auto';
        chart.canvas.parentNode.style.position ||= 'relative';
        chart.canvas.parentNode.appendChild(el);
      }

      if (tooltip.opacity === 0) {
        el.style.opacity = 0
        return
      }

      const dp = tooltip.dataPoints[0]
      const index = dp.dataIndex
      const date = dp.label
      const total = dp.raw
      const rows = labelsFromMeta[index] || []

      const amount_color_class = {
        positive: 'text-success-600',
        negative: 'text-danger-600',
        zero: 'text-dark'
      }

      const cssTotalClass =
        total > 0 ? amount_color_class.positive :
        total < 0 ? amount_color_class.negative :
        amount_color_class.zero

      const fmt = new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })

      const linesHtml = rows.map((row) => {
        const amount = Number(row.amount) || 0;
        const sign = amount > 0 ? '+' : amount < 0 ? '−' : '';
        const color = amount > 0 ? 'success-600' : amount < 0 ? 'danger-600' : 'dark';
        return `
          <div>
            <span>${row.label} :</span>
            <span class="text-${color}">${sign}${fmt.format(Math.abs(amount))}</span>
          </div>
        `
      }).join('')

      el.innerHTML = `
        <div class="bg-white px-3 py-2 rounded shadow-lg text-xs text-nowrap">
          <div class="text-gray-700">${date}</div>
          <div class="${cssTotalClass} fw-medium">${fmt.format(total)}</div>
          <div>
            ${linesHtml}
          </div>
        </div>
      `

      const canvasRect = chart.canvas.getBoundingClientRect();
      const tooltipWidth = 15 * 16;
      const tooltipHeight = el.offsetHeight;

      let left = tooltip.caretX;
      let top = tooltip.caretY - 10;

      left = Math.min(Math.max(left, tooltipWidth / 2), canvasRect.width - tooltipWidth / 2);
      top = Math.max(top, tooltipHeight / 2);

      el.style.left = left + 'px';
      el.style.top = top + 'px';
      el.style.opacity = 1;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external
        }
      },
      scales: {
        y: { display: false },
        x: {
          grid: { display: false },
        },
      }
    };
  }
}
